import FilterPill from '../dashboard/FilterPill.tsx';
import TileSizeControl from '../../components/TileSizeControl.tsx';
import { LayoutGrid, Folder, Inbox, Rows, ArrowUpDown } from '../../lib/icons.tsx';
import type { LibraryFilterBarModel } from './libraryFilterOptions.ts';
import type { LibraryGroupMode, LibrarySort, LibraryTypeFilter, LibraryUsedFilter } from './libraryFilterState.ts';
import type { TileSize } from '../../components/TileSizeControl.tsx';

/**
 * The Library's filter row: the three axes that narrow the body, then grouping,
 * sort and the card size pushed to the far end.
 *
 * The same arrangement as `routes/dashboard/FilterBar`, and the same `FilterPill`
 * rendered five times — the pill knows nothing about decks or about items, it
 * is handed a label, a value and a list. Only the model differs, which is the
 * whole point of keeping it in `libraryFilterOptions.ts`.
 *
 * The spacer is load-bearing here for the same reason it is there: nothing to
 * its right changes WHICH items show, which is what lets the chip row
 * underneath speak for the left cluster alone. Group sits on the right despite
 * being a menu of two, because grouping rearranges the same items rather than
 * removing any — and for the same reason neither it nor Sort ever inverts.
 */

type Props = {
  model: LibraryFilterBarModel;
  onType: (v: LibraryTypeFilter) => void;
  onCategory: (v: string | null) => void;
  onUsed: (v: LibraryUsedFilter) => void;
  onGroup: (v: LibraryGroupMode) => void;
  onSort: (v: LibrarySort) => void;
  tileSize: TileSize;
  onTileSize: (v: TileSize) => void;
};

export default function LibraryFilterBar({
  model,
  onType,
  onCategory,
  onUsed,
  onGroup,
  onSort,
  tileSize,
  onTileSize,
}: Props) {
  return (
    <div className="filter-bar" role="group" aria-label="Filter and sort library items">
      <FilterPill<LibraryTypeFilter>
        label={model.type.label}
        value={model.type.value}
        valueLabel={model.type.valueLabel}
        options={model.type.options}
        onChange={onType}
        icon={<LayoutGrid />}
        active={model.type.value !== null}
      />
      <FilterPill<string | null>
        label={model.category.label}
        value={model.category.value}
        valueLabel={model.category.valueLabel}
        options={model.category.options}
        onChange={onCategory}
        icon={<Folder />}
        active={model.category.value !== null}
      />
      <FilterPill<LibraryUsedFilter>
        label={model.used.label}
        value={model.used.value}
        valueLabel={model.used.valueLabel}
        options={model.used.options}
        onChange={onUsed}
        icon={<Inbox />}
        active={model.used.value !== null}
      />
      <span className="filter-bar__spacer" />
      <FilterPill<LibraryGroupMode>
        label={model.group.label}
        value={model.group.value}
        valueLabel={model.group.valueLabel}
        options={model.group.options}
        onChange={onGroup}
        icon={<Rows />}
      />
      <FilterPill<LibrarySort>
        label={model.sort.label}
        value={model.sort.value}
        valueLabel={model.sort.valueLabel}
        options={model.sort.options}
        onChange={onSort}
        icon={<ArrowUpDown />}
      />
      <TileSizeControl value={tileSize} onChange={onTileSize} />
    </div>
  );
}
